import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLogs, setActivityLogs] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const fetchDashboardData = useCallback(async () => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const [statsRes, activityRes] = await Promise.all([
        axios.get(getApiUrl(API_ENDPOINTS.adminStats), { headers }),
        axios.get(getApiUrl('/api/admin/activity-logs'), { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setActivityLogs(activityRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    // Check if user is logged in
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchDashboardData();
  }, [token, navigate, fetchDashboardData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'LEAD_CLAIMED': return '👨‍💼';
      case 'LEAD_UNCLAIMED': return '🔄';
      case 'LEAD_STATUS_UPDATED': return '📝';
      case 'LEAD_NOTES_UPDATED': return '🗒️';
      case 'LEAD_STATUS_AND_NOTES_UPDATED': return '📋';
      case 'LEAD_CREATED': return '➕';
      case 'LEAD_DELETED': return '🗑️';
      default: return '📊';
    }
  };

  const exportActivityLogs = async () => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };
      
      let url = getApiUrl('/api/admin/activity-logs/export');
      const params = new URLSearchParams();
      
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, { 
        headers,
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `activity-logs-${dateFrom || 'all'}-to-${dateTo || 'now'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      alert('Error exporting activity logs. Please try again.');
    }
  };






  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">Performance Dashboard</h1>
        <button
          onClick={fetchDashboardData}
          className="refresh-button"
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Performance Overview Section */}
      <div className="performance-overview">
        <h2>📊 Performance Overview</h2>
        <div className="performance-cards">
          <div className="performance-card ben">
            <div className="perf-header">
              <div className="perf-name">👨 Ben Fishman</div>
            </div>
            <div className="perf-stats">
              <div className="perf-stat">
                <span className="perf-label">Active Leads</span>
                <span className="perf-value">{stats?.benLeads?.filter(l => l.status !== 'lost' && l.status !== 'closed').length || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Closed Deals</span>
                <span className="perf-value">{stats?.benLeads?.filter(l => l.status === 'closed').length || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Conversion Rate</span>
                <span className="perf-value">
                  {stats?.benLeads?.length > 0 
                    ? ((stats.benLeads.filter(l => l.status === 'closed').length / stats.benLeads.length) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Total Assigned</span>
                <span className="perf-value">{stats?.benLeads?.length || 0}</span>
              </div>
            </div>
          </div>
          
          <div className="performance-card stef">
            <div className="perf-header">
              <div className="perf-name">👩 Stefanie Fishman</div>
            </div>
            <div className="perf-stats">
              <div className="perf-stat">
                <span className="perf-label">Active Leads</span>
                <span className="perf-value">{stats?.stefLeads?.filter(l => l.status !== 'lost' && l.status !== 'closed').length || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Closed Deals</span>
                <span className="perf-value">{stats?.stefLeads?.filter(l => l.status === 'closed').length || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Conversion Rate</span>
                <span className="perf-value">
                  {stats?.stefLeads?.length > 0 
                    ? ((stats.stefLeads.filter(l => l.status === 'closed').length / stats.stefLeads.length) * 100).toFixed(1)
                    : 0}%
                  </span>
                </div>
              <div className="perf-stat">
                <span className="perf-label">Total Assigned</span>
                <span className="perf-value">{stats?.stefLeads?.length || 0}</span>
              </div>
            </div>
          </div>
          
          <div className="performance-card total">
            <div className="perf-header">
              <div className="perf-name">📈 Overall Stats</div>
            </div>
            <div className="perf-stats">
              <div className="perf-stat">
                <span className="perf-label">Total Referrals</span>
                <span className="perf-value">{stats?.totalReferrals || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Active Leads</span>
                <span className="perf-value">{stats?.claimedLeads || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Closed Deals</span>
                <span className="perf-value">{stats?.statusCounts?.closed || 0}</span>
              </div>
              <div className="perf-stat">
                <span className="perf-label">Conversion Rate</span>
                <span className="perf-value">{stats?.conversionRate || 0}%</span>
              </div>
            </div>
          </div>
                  </div>
                </div>

      {/* Recent Activity Section */}
      <div className="activity-section">
        <div className="activity-header">
          <h3>🕒 Recent Activity</h3>
          <div className="activity-controls">
            <div className="date-range">
              <label>From:</label>
              <input 
                type="date" 
                className="date-input"
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <label>To:</label>
              <input 
                type="date" 
                className="date-input"
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <button 
              onClick={exportActivityLogs}
              className="export-btn"
            >
              📊 Export
            </button>
          </div>
      </div>
        <div className="activity-container">
          {activityLogs.length === 0 ? (
            <div className="no-activity">
              <p>No recent activity to display</p>
            </div>
          ) : (
            <div className="activity-list">
              {activityLogs.slice(0, 50).map(activity => (
                <div key={activity.activityId} className="activity-item">
                  <div className="activity-icon">
                    {getActivityIcon(activity.action)}
                  </div>
                  <div className="activity-content">
                    <div className="activity-header">
                      <span className="activity-user">{activity.user}</span>
                      <span className="activity-action">{activity.details.action}</span>
                    </div>
                    <div className="activity-details">
                      {activity.details.leadId && (
                        <span className="activity-lead-id">Lead: {activity.details.leadId}</span>
                      )}
                      {activity.details.claimedBy && (
                        <span className="activity-claimed-by">Assigned to: {activity.details.claimedBy}</span>
                      )}
                      {activity.details.newStatus && (
                        <span className="activity-status">Status: {activity.details.newStatus}</span>
                      )}
                    </div>
                    <div className="activity-time">
                      {formatRelativeTime(activity.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {activityLogs.length > 50 && (
            <div className="activity-footer">
              <p>Showing latest 50 activities. Use export for full history.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 